import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuroraHeader, AuroraFooter } from "../components/aurora/Header";
import { api } from "../lib/api";
import { ADMIN_STATUSES, ADMIN_STATUS_COLORS } from "../lib/countries";
import { LogOut, Star, Archive } from "lucide-react";

const VIEWS = [
  { key: "active", label: "Active" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const AdminStatusBadge = ({ status }) => {
  const c = ADMIN_STATUS_COLORS[status] || "#6B5E4A";
  return (
    <span
      className="inline-block px-3 py-1 text-[10px] font-heading uppercase tracking-[0.22em] border"
      style={{ color: c, borderColor: c }}
      data-testid={`admin-status-badge-${status}`}
    >
      {status}
    </span>
  );
};

const TableRow = ({ s, onStar, onSetAdminStatus }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const toggleStar = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onStar(s.submissionId, !s.starred);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (e) => {
    e.stopPropagation();
    const next = e.target.value;
    if (next === s.adminStatus) return;
    await onSetAdminStatus(s.submissionId, next);
  };

  const archive = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (s.adminStatus === "Archived") return;
    await onSetAdminStatus(s.submissionId, "Archived");
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/admin/submissions/${s.submissionId}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(`/admin/submissions/${s.submissionId}`);
      }}
      className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-[#A88059]/20 hover:bg-[#A88059]/5 transition-colors items-center cursor-pointer"
      data-testid={`row-${s.submissionId}`}
    >
      {/* Star */}
      <div className="col-span-1 flex items-center">
        <button
          onClick={toggleStar}
          type="button"
          aria-label={s.starred ? "Unstar submission" : "Star submission"}
          className="p-1 -m-1 hover:opacity-80 transition-opacity"
          data-testid={`btn-star-${s.submissionId}`}
        >
          <Star
            size={18}
            className={s.starred ? "text-[#D49A36]" : "text-[#A88059]/40"}
            fill={s.starred ? "#D49A36" : "none"}
          />
        </button>
      </div>

      {/* Company */}
      <Link
        to={`/admin/submissions/${s.submissionId}`}
        onClick={(e) => e.stopPropagation()}
        className="col-span-3 font-heading text-[#190F08] text-base hover:underline"
      >
        {s.companyName || <span className="text-[#6B5E4A]/60">— Untitled —</span>}
      </Link>

      {/* Email */}
      <div className="col-span-3 font-body text-sm text-[#6B5E4A] break-all">
        {s.clientEmail || "—"}
      </div>

      {/* Date */}
      <div className="col-span-1 font-body text-sm text-[#6B5E4A]">
        {s.createdAt ? new Date(s.createdAt).toLocaleDateString("en-GB") : "—"}
      </div>

      {/* Admin workflow status dropdown + tech status hint */}
      <div className="col-span-3 flex items-center gap-2 flex-wrap">
        <select
          value={s.adminStatus}
          onChange={changeStatus}
          onClick={(e) => e.stopPropagation()}
          className="aurora-select-inline"
          style={{
            borderColor: ADMIN_STATUS_COLORS[s.adminStatus] || "#A88059",
            color: ADMIN_STATUS_COLORS[s.adminStatus] || "#6B5E4A",
          }}
          data-testid={`select-admin-status-${s.submissionId}`}
        >
          {ADMIN_STATUSES.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        {s.edited && (
          <span
            className="inline-block px-1.5 py-0.5 text-[9px] font-heading uppercase tracking-[0.22em] border border-[#D49A36] text-[#D49A36]"
            data-testid={`edited-badge-${s.submissionId}`}
          >
            Edited
          </span>
        )}
      </div>

      {/* Archive action */}
      <div className="col-span-1 flex justify-end">
        {s.adminStatus !== "Archived" && (
          <button
            onClick={archive}
            type="button"
            aria-label="Archive submission"
            className="p-1 -m-1 text-[#6B5E4A]/70 hover:text-[#190F08] transition-colors"
            data-testid={`btn-archive-${s.submissionId}`}
          >
            <Archive size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

const renderTableBody = (loading, items, onStar, onSetAdminStatus) => {
  if (loading) {
    return <div className="px-6 py-10 text-center label-eyebrow text-[#6B4D2B]">Loading...</div>;
  }
  if (items.length === 0) {
    return (
      <div className="px-6 py-10 text-center font-body text-[#6B5E4A]">No submissions found</div>
    );
  }
  return items.map((s) => (
    <TableRow
      key={s.submissionId}
      s={s}
      onStar={onStar}
      onSetAdminStatus={onSetAdminStatus}
    />
  ));
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (v) => {
      setLoading(true);
      try {
        const r = await api.adminList(v);
        setItems(r.data.items || []);
      } catch (e) {
        if (e?.response?.status === 401) {
          navigate("/admin/login");
          return;
        }
        toast.error("Failed to load submissions");
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    load(view);
  }, [view, load]);

  const onStar = async (id, starred) => {
    try {
      await api.adminSetStar(id, starred);
      setItems((prev) => prev.map((it) => (it.submissionId === id ? { ...it, starred } : it)));
      toast.success(starred ? "Starred" : "Unstarred");
    } catch (e) {
      toast.error("Failed to update star");
    }
  };

  const onSetAdminStatus = async (id, adminStatus) => {
    try {
      await api.adminSetAdminStatus(id, adminStatus);
      if (view === "active" && adminStatus === "Archived") {
        // Row leaves the active view — drop it locally
        setItems((prev) => prev.filter((it) => it.submissionId !== id));
      } else if (view === "archived" && adminStatus !== "Archived") {
        setItems((prev) => prev.filter((it) => it.submissionId !== id));
      } else {
        setItems((prev) =>
          prev.map((it) => (it.submissionId === id ? { ...it, adminStatus } : it))
        );
      }
      toast.success(`Status → ${adminStatus}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const logout = async () => {
    try {
      await api.adminLogout();
    } catch (err) {
      console.error("logout failed", err);
    }
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <AuroraHeader compact />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow text-[#6B4D2B] mb-2">Aurora Console</div>
            <h2 className="font-heading-bold text-4xl text-[#190F08]">Submissions Dashboard</h2>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout">
            <LogOut size={14} className="inline mr-2" /> Sign out
          </button>
        </div>

        <hr className="gold-rule mb-8" />

        <div className="flex items-center gap-3 flex-wrap mb-6" data-testid="view-filters">
          {VIEWS.map((v) => {
            const active = view === v.key;
            return (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className="px-4 py-2 font-heading text-[11px] tracking-[0.22em] uppercase border transition-colors"
                style={{
                  borderColor: active ? "#A88059" : "rgba(168,128,89,0.3)",
                  background: active ? "#A88059" : "transparent",
                  color: active ? "#FAF5EE" : "#6B5E4A",
                }}
                data-testid={`view-${v.key}`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        <div className="border border-[#A88059]/40" data-testid="submissions-table">
          <div className="grid grid-cols-12 gap-3 px-6 py-3 border-b border-[#A88059]/40 bg-[#A88059]/5 label-eyebrow">
            <div className="col-span-1"></div>
            <div className="col-span-3">Company</div>
            <div className="col-span-3">Client Email</div>
            <div className="col-span-1">Sent</div>
            <div className="col-span-3">Workflow</div>
            <div className="col-span-1 text-right">Archive</div>
          </div>
          {renderTableBody(loading, items, onStar, onSetAdminStatus)}
        </div>
      </main>
      <AuroraFooter />
    </div>
  );
}
